import cx from 'classnames'
import { navigate } from 'gatsby'
import React from 'react'
import { Field, Form, FormRenderProps } from 'react-final-form'

import { Primary } from '@cinch/oxd-components/button/src'
import { Valuation } from '@cinch/oxd-components/icons/src'
import LoadingSpinner from '@cinch/oxd-components/loading-spinner/src'
import Panel from '@cinch/oxd-components/panel/src/panel.component'
import SVG from '@cinch/oxd-components/svg/src'
import Text, { TextStyles } from '@cinch/oxd-components/text/src'
import useToken from '@cinch/oxd-pages/wbac/src/hooks/token.hook'
import submitVrm from '@cinch/oxd-pages/wbac/src/stages/landing-page/submit-vrm'
import Statuses from '@cinch/oxd-utils/request-status/src'
import { parseNumberPlate, validateNumberPlate } from '@cinch/oxd-utils/validators/src'
import { FEATURES, hasFeatureFlag } from '@cinch/oxd-utils/feature-flags/src'

import * as styles from './valuation-input-panel.module.css'

const ValuationInputPanel: React.FC<{
  i18n: Cinch.WebAppTranslations['valuationModule']
  isWide?: boolean
}> = ({ i18n, isWide = true }) => {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  // prevent call to WBAC auth on homepage load
  let status: string
  if (hasFeatureFlag(FEATURES.SELL_HOLD)) {
    status = Statuses.ERROR
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const token = useToken()
    status = token.status
  }
  const isTokenPending = status === Statuses.PENDING
  const hasError = status === Statuses.ERROR

  const handleWbacSubmit = (vrm: string) => {
    setIsSubmitting(true)
    submitVrm(vrm, navigate).then((error) => {
      if (error) {
        navigate(`/sell/check-registration/${vrm}`)
      }
    })
  }

  const handleFormSubmit = (values: { [key: string]: any }) => {
    if (!hasError) {
      handleWbacSubmit(values.reg)
    } else {
      navigate(`/sell/check-registration/${values.reg}`)
    }
  }

  const render = (formProps: FormRenderProps) => {
    const { title, body, inputLabel, placeholder, submitLabel } = i18n
    const { handleSubmit, values = {} } = formProps
    const buttonBody = isTokenPending || isSubmitting ? <LoadingSpinner /> : submitLabel
    const isButtonDisabled = !!validateNumberPlate(values.reg)

    return (
      <form onSubmit={handleSubmit}>
        <Panel>
          <div
            data-testid="valuationInputPanel"
            className={cx(styles.valuationInputPanel, {
              [styles.valuationInputPanelWide]: isWide,
            })}
          >
            <SVG
              hasGradient={true}
              gradientId={'bravo'}
              children={Valuation}
              className={cx(styles.icon, { [styles.iconWide]: isWide })}
            />
            <div className={cx(styles.body, { [styles.bodyWide]: isWide })}>
              <Text className={styles.title}>{title}</Text>
              <Text element="p" type={TextStyles.body1}>
                {body}
              </Text>
              <div className={cx(styles.inputWrapper, { [styles.inputWrapperWide]: isWide })}>
                <Text type={TextStyles.caption} element="label" className={styles.inputLabel} htmlFor="reg">
                  {inputLabel}
                  <Field
                    name="reg"
                    component="input"
                    parse={parseNumberPlate}
                    validate={validateNumberPlate}
                    placeholder={placeholder}
                    className={styles.input}
                    data-testid="text-input"
                  />
                </Text>
                {isWide && (
                  <Primary
                    type="submit"
                    oxdClassName={styles.submitWide}
                    disabled={isButtonDisabled}
                    data-testid="valuationInputPanel__btn"
                    oxford={true}
                  >
                    {buttonBody}
                  </Primary>
                )}
              </div>
            </div>
            {!isWide && (
              <Primary
                type="submit"
                oxdClassName={styles.submit}
                disabled={isButtonDisabled}
                data-testid="valuationInputPanel__btn"
                oxford={true}
              >
                {buttonBody}
              </Primary>
            )}
          </div>
        </Panel>
      </form>
    )
  }

  return <Form onSubmit={handleFormSubmit} initialValues={{}} render={render} />
}

export default ValuationInputPanel
